import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../interceptors";

// Manage roles — list, create, edit, delete, and assign feature permissions.
// Redesigned to match the app's modern surfaces (cards + vf utility classes),
// replacing the legacy Materialize TableComponent + redux + nested routes.
//
// APIs:
//   POST /common/base/roles/get-filtered   { filterList, sortList, pagingNo, pageSize }
//   POST /common/base/roles/save           { id?, name, description }
//   POST /common/base/roles/trash          { id }
//   POST /common/base/features/features     -> flat feature nodes (dot-notation ids)
//   POST /common/base/role-feature/add     { roleId, featureIds }

// Build a nested tree from the flat, dot-notation feature list (e.g. "1", "1.2").
const convertData = (nodes = []) => {
  const build = (parentId = "") =>
    nodes
      .filter((node) =>
        parentId === ""
          ? !String(node.id).includes(".")
          : String(node.id).startsWith(parentId) &&
            String(node.id).split(".").length ===
              parentId.split(".").length + 1,
      )
      .map((node) => ({ ...node, children: build(String(node.id)) }));
  return build();
};

// All descendant ids (including the node itself) — used to toggle a subtree.
const subtreeIds = (node) => {
  const ids = [node.id];
  (node.children || []).forEach((c) => ids.push(...subtreeIds(c)));
  return ids;
};

// Comma-joined descriptions for a role's assigned feature ids (summary cell).
const summarize = (treeData, featureSetList = []) => {
  const set = new Set(featureSetList);
  const walk = (nodes) => {
    let out = [];
    for (const n of nodes) {
      if (set.has(n.id)) out.push(n.description || n.name || n.id);
      if (n.children?.length) out = out.concat(walk(n.children));
    }
    return out;
  };
  return walk(treeData);
};

export default function ManageRoles() {
  const [roles, setRoles] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);

  const [formModal, setFormModal] = useState(null); // { role? } — create/edit
  const [permModal, setPermModal] = useState(null); // role being permissioned

  const flashToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axiosInstance.post("/common/base/roles/get-filtered", {
        filterList: [],
        sortList: [],
        pagingNo: 1,
        pageSize: 200,
      });
      setRoles(Array.isArray(r.data?.list) ? r.data.list : []);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Failed to load roles.",
      );
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFeatures = async () => {
    try {
      const r = await axiosInstance.post("/common/base/features/features");
      if (r.data?.success) setTreeData(convertData(r.data.list || []));
    } catch {
      /* tree just stays empty; permissions modal will say so */
    }
  };

  useEffect(() => {
    document.title = "Manage roles - Ample Hire";
    loadRoles();
    loadFeatures();
  }, []);

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      const r = await axiosInstance.post("/common/base/roles/trash", {
        id: role.id,
      });
      if (r.data?.success === false) {
        flashToast(r.data?.message || "Failed to delete role.", "err");
        return;
      }
      setRoles((prev) => prev.filter((x) => x.id !== role.id));
      flashToast("Role deleted.");
    } catch (e) {
      flashToast(
        e.response?.data?.message || "Failed to delete role.",
        "err",
      );
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q),
    );
  }, [roles, search]);

  return (
    <div className="max-w-[1200px] mx-auto px-2 py-2 font-sans">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-vf-base font-heading">
            Manage roles
          </h1>
          <p className="text-sm text-vf-muted mt-1">
            Define roles and the feature permissions each one grants.
          </p>
        </div>
        <button
          onClick={() => setFormModal({})}
          className="btn-primary btn-sm"
        >
          + Create role
        </button>
      </div>

      {toast && (
        <div
          className={`rounded-xl px-4 py-3 text-sm mb-4 ${
            toast.kind === "ok" ? "badge-success" : "badge-danger"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Search bar */}
      <div className="card p-0 overflow-hidden mb-5">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-vf-muted"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              className="input pl-9 w-64"
              placeholder="Search roles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="text-xs text-vf-muted">{filtered.length} roles</div>
        </div>
      </div>

      {/* Roles table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-center">
            <div className="skeleton h-4 w-32 mx-auto rounded" />
          </div>
        ) : error ? (
          <div className="px-5 py-12 text-center text-sm text-vf-danger">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-vf-muted">
            {roles.length === 0
              ? "No roles yet. Click + Create role to add one."
              : "No roles match your search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="tbl-header">
                  <th className="text-left px-4 py-3 w-[20%]">Role</th>
                  <th className="text-left px-4 py-3 w-[30%]">Description</th>
                  <th className="text-left px-4 py-3 w-[34%]">Permissions</th>
                  <th className="px-4 py-3 w-[16%]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((role) => {
                  const perms = summarize(treeData, role.featureSetList || []);
                  return (
                    <tr key={role.id} className="tbl-row">
                      <td className="px-4 py-3 font-medium text-vf-base">
                        {role.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-vf-muted">
                        {role.description || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-vf-muted truncate max-w-[260px]">
                            {perms.length > 0
                              ? perms.slice(0, 3).join(", ") +
                                (perms.length > 3
                                  ? ` +${perms.length - 3} more`
                                  : "")
                              : "No permissions"}
                          </span>
                          <button
                            onClick={() => setPermModal(role)}
                            className="btn-ghost btn-sm text-xs text-vf-primary"
                          >
                            Manage
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setFormModal({ role })}
                          className="btn-secondary btn-sm mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteRole(role)}
                          className="btn-danger btn-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formModal && (
        <RoleFormModal
          role={formModal.role}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            flashToast(formModal.role ? "Role updated." : "Role created.");
            loadRoles();
          }}
        />
      )}

      {permModal && (
        <PermissionsModal
          role={permModal}
          treeData={treeData}
          onClose={() => setPermModal(null)}
          onSaved={(featureIds) => {
            setRoles((prev) =>
              prev.map((r) =>
                r.id === permModal.id
                  ? { ...r, featureSetList: featureIds }
                  : r,
              ),
            );
            setPermModal(null);
            flashToast("Permissions saved.");
          }}
        />
      )}
    </div>
  );
}

function RoleFormModal({ role, onClose, onSaved }) {
  const editing = !!role;
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (!name.trim()) {
      setError("Role name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), description: description.trim() };
      if (editing) body.id = role.id;
      const r = await axiosInstance.post("/common/base/roles/save", body);
      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Failed to save role.");
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to save role.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-vf-base">
            {editing ? "Edit role" : "Create role"}
          </h2>
          <button
            onClick={onClose}
            className="text-vf-muted hover:text-vf-base text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Role name <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g. Recruiter"
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-vf-accent focus:ring-1 focus:ring-vf-highlight"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Description
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What this role can do"
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-vf-accent focus:ring-1 focus:ring-vf-highlight"
            />
          </label>
        </div>
        {error && (
          <div className="mt-3 px-3 py-2 bg-red-50 text-red-700 text-xs rounded">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create role"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PermissionsModal({ role, treeData, onClose, onSaved }) {
  const [checked, setChecked] = useState(() => new Set(role.featureSetList || []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Toggling a node toggles its whole subtree.
  const toggleNode = (node, isChecked) => {
    const ids = subtreeIds(node);
    setChecked((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (isChecked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const featureIds = Array.from(checked);
      const r = await axiosInstance.post("/common/base/role-feature/add", {
        roleId: role.id,
        featureIds,
      });
      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Failed to save permissions.");
      }
      onSaved(featureIds);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Failed to save permissions.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-lg font-semibold text-vf-base">Permissions</h2>
            <p className="text-xs text-vf-muted mt-0.5">{role.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-vf-muted hover:text-vf-base text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mt-3 flex-1 overflow-y-auto border border-slate-200 rounded-lg p-3">
          {treeData.length === 0 ? (
            <div className="text-sm text-vf-muted py-8 text-center">
              No features available.
            </div>
          ) : (
            <PermissionTree
              nodes={treeData}
              checked={checked}
              onToggle={toggleNode}
            />
          )}
        </div>

        {error && (
          <div className="mt-3 px-3 py-2 bg-red-50 text-red-700 text-xs rounded">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-4">
          <span className="text-xs text-vf-muted">
            {checked.size} permission{checked.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded border border-slate-300 text-xs"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save permissions"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Recursive permission tree with Tailwind checkboxes. A parent reflects checked
// when every descendant is checked, and indeterminate when only some are.
function PermissionTree({ nodes, checked, onToggle, depth = 0 }) {
  return (
    <ul className={depth === 0 ? "space-y-1" : "space-y-1 mt-1"}>
      {nodes.map((node) => {
        const all = subtreeIds(node);
        const selectedCount = all.filter((id) => checked.has(id)).length;
        const isChecked = selectedCount === all.length;
        const isIndeterminate = selectedCount > 0 && !isChecked;
        return (
          <li key={node.id} style={{ paddingLeft: depth ? 16 : 0 }}>
            <label className="flex items-center gap-2 text-sm text-vf-base cursor-pointer py-0.5">
              <input
                type="checkbox"
                checked={isChecked}
                ref={(el) => el && (el.indeterminate = isIndeterminate)}
                onChange={(e) => onToggle(node, e.target.checked)}
                className="accent-vf-primary"
              />
              <span>{node.description || node.name || node.id}</span>
            </label>
            {node.children?.length > 0 && (
              <PermissionTree
                nodes={node.children}
                checked={checked}
                onToggle={onToggle}
                depth={depth + 1}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
